import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { z } from 'zod';
import type {
  OpportunityNote,
  PaginatedOpportunityNoteList,
  PatchedOpportunityNote,
} from '../../../../../js/api';
import {
  PatchedOpportunityStatusUpdate,
  PatchedOpportunityUpdate,
  AssignmentsService,
} from '../../../../../js/api';
import { handleApiError } from '../../../../../js/app/utils/errorhandling';
import { ActivityLog } from '../../../components/activity-log';
import { BrandSwitch } from '../../../components/BrandSwitch';
import { Button } from '../../../components/button';
import { Dialog, DialogBody } from '../../../components/dialog';
import { DrawerCloseButton } from '../../../components/drawer';
import { DrawerSectionHeader, DrawerField } from '../../../components/drawer-components';
import { FieldValidationErrorMessage } from '../../../components/field-validation-error-message';
import { Fieldset, Field } from '../../../components/fieldset';
import { InlineDateEdit } from '../../../components/inline-date-edit';
import { InlineTextEdit } from '../../../components/inline-text-edit';
import { PageHeader } from '../../../components/PageHeader';
import { StaffingEntityQuickDetails } from '../../../components/staffing-entity-quick-details';
import { StatusTransitionModal } from '../../../components/status-transition-modal';
import { StatusPill } from '../../../components/StatusPill';
import { Tabs, TabsList, TabsButton, TabsContent } from '../../../components/tabs';
import { Textarea } from '../../../components/textarea';
import { MENU_BADGES_QUERY_KEY, QUEUE_NOTIFICATIONS_QUERY_KEY } from '../../../constants/queryKeys';
import { WorkflowScopes } from '../../../constants/workflows';
import { useCodeDefinitionsAsMaps } from '../../../hooks/useCodeDefinitions';
import { StatusMetadata, useWorkflowMetadata } from '../../../hooks/useWorkflowMetadata';
import type { ActivityStatusHistory, ActivityHistory } from '../../../utils/activity-types';
import { formatDateTo24Hr, parseApiLocalDate, parseFormDate } from '../../../utils/date';
import { getFacilityLocationDisplayValue } from '../../../utils/facility';
import { StatusTransitionButtons } from '../../../utils/statusTransitions';
import OpportunityForm from './OpportunityForm';
import {
  OPPORTUNITIES_LIST_QUERY_KEY,
  OPPORTUNITY_NOTES_QUERY_KEY,
  OPPORTUNITY_QUERY_KEY,
  OPPORTUNITY_STATUS_HISTORY_QUERY_KEY,
  OPPORTUNITY_CHANGE_HISTORY_QUERY_KEY,
} from './queryKeys';

type OpportunityDrawerProps = {
  opportunityId: string;
  onClose?: () => void;
};

const WORKFLOWS_META_QUERY_KEY = 'assignments.opportunity';

const noteFormSchema = z.object({
  comment: z.string().min(1, 'Comment is required'),
});

type NoteFormData = z.infer<typeof noteFormSchema>;

function useDebouncedFn<T extends (...args: any[]) => void>(fn: T, delayMs: number) {
  const fnRef = useRef(fn);
  fnRef.current = fn;

  return useMemo(() => {
    let t: any;
    const debounced = (...args: any[]) => {
      clearTimeout(t);
      t = setTimeout(() => fnRef.current(...args), delayMs);
    };
    debounced.flush = (...args: any[]) => {
      clearTimeout(t);
      fnRef.current(...args);
    };
    debounced.cancel = () => clearTimeout(t);
    return debounced as T & { flush: T; cancel: () => void };
  }, [delayMs]);
}

// eslint-disable-next-line sonarjs/cognitive-complexity
export function OpportunityDrawer({ opportunityId, onClose }: OpportunityDrawerProps) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [autoMatch, setAutoMatch] = useState<boolean | null>(null);
  const [maxAcceptedProviders, setMaxAcceptedProviders] = useState<string>('');
  const lastSavedMaxAcceptedProvidersRef = useRef<number | null>(null);
  const isSavingMaxAcceptedProvidersRef = useRef(false);
  const isMaxAcceptedProvidersDirtyRef = useRef(false);
  const [startDate, setStartDate] = useState<Date | null>(null);
  const lastSavedStartDateRef = useRef<string | null>(null);
  const isSavingStartDateRef = useRef(false);
  const isStartDateDirtyRef = useRef(false);
  const [isStatusModalOpen, setIsStatusModalOpen] = useState(false);
  const [pendingTargetStatus, setPendingTargetStatus] = useState<string | null>(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [headerProps, setHeaderProps] = useState<{
    title: string;
    buttonHtml: React.ReactNode;
  } | null>(null);

  const { data: codeDefinitionMaps } = useCodeDefinitionsAsMaps([
    'Specialty',
    'Degree',
    'WorkType',
    'ShiftRateTypes',
  ]);

  const {
    data: opportunity,
    isLoading: isLoadingOpportunity,
    error: opportunityError,
  } = useQuery({
    queryKey: [OPPORTUNITY_QUERY_KEY, opportunityId],
    queryFn: () => AssignmentsService.assignmentsOpportunitiesRetrieve({ id: opportunityId }),
    enabled: !!opportunityId,
  });

  const { statuses: workflowStatuses } = useWorkflowMetadata(WORKFLOWS_META_QUERY_KEY);

  const { data: notesData } = useQuery({
    queryKey: [OPPORTUNITY_NOTES_QUERY_KEY, opportunityId],
    queryFn: () =>
      AssignmentsService.assignmentsOpportunitiesNotesList({
        id: opportunityId,
        ordering: '-created_at', // Most recent first
      }),
    enabled: !!opportunityId,
    select: (data: PaginatedOpportunityNoteList | OpportunityNote | OpportunityNote[]) => {
      if (!data) {
        return [];
      }
      // Handle array of notes
      if (Array.isArray(data)) {
        return data;
      }
      // Handle paginated response
      if ('results' in data) {
        return data.results || [];
      }
      // Handle single note
      return [data as OpportunityNote];
    },
  });

  const { data: statusHistory } = useQuery({
    queryKey: [OPPORTUNITY_STATUS_HISTORY_QUERY_KEY, opportunityId],
    queryFn: () =>
      AssignmentsService.assignmentsOpportunitiesStatusHistoryRetrieve({
        id: opportunityId,
        sort: 'desc',
      }),
    enabled: !!opportunityId,
  });

  const { data: changeHistory } = useQuery({
    queryKey: [OPPORTUNITY_CHANGE_HISTORY_QUERY_KEY, opportunityId],
    queryFn: () =>
      AssignmentsService.assignmentsOpportunitiesChangeHistoryRetrieve({
        id: opportunityId,
        sort: 'desc',
      }),
    enabled: !!opportunityId,
  });

  const noteForm = useForm<NoteFormData>({
    resolver: zodResolver(noteFormSchema),
    defaultValues: {
      comment: '',
    },
  });

  const createNoteMutation = useMutation({
    mutationFn: async (data: NoteFormData) => {
      return AssignmentsService.assignmentsOpportunitiesNotesCreate({
        id: opportunityId,
        requestBody: {
          opportunity: opportunityId,
          comment: data.comment,
        } as OpportunityNote,
      });
    },
    onSuccess: () => {
      toast.success('Note added successfully');
      noteForm.reset();
      queryClient.invalidateQueries({ queryKey: [OPPORTUNITY_NOTES_QUERY_KEY, opportunityId] });
    },
    onError: (error: Error) => {
      handleApiError(error, { messagePrefix: 'Error adding note' });
    },
  });

  useEffect(() => {
    if (opportunity) {
      setAutoMatch(opportunity.settings?.auto_match ?? true);
      // Store the initial value to compare against in the debounced effect
      if (!isSavingMaxAcceptedProvidersRef.current) {
        setMaxAcceptedProviders(opportunity.settings?.max_accepted_connections?.toString() ?? '');
        lastSavedMaxAcceptedProvidersRef.current =
          opportunity.settings?.max_accepted_connections ?? null;
        isMaxAcceptedProvidersDirtyRef.current = false;
      }
      if (!isSavingStartDateRef.current) {
        setStartDate(parseApiLocalDate(opportunity.starts_on));
        lastSavedStartDateRef.current = opportunity.starts_on ?? null;
        isStartDateDirtyRef.current = false;
      }
    }
  }, [opportunity]);

  const updateAutoMatchMutation = useMutation({
    mutationFn: async (value: boolean) => {
      return AssignmentsService.assignmentsOpportunitiesPartialUpdate({
        id: opportunityId,
        requestBody: {
          settings: {
            ...opportunity?.settings,
            auto_match: value,
          },
        } as PatchedOpportunityUpdate,
      });
    },
    onSuccess: async (data, variables) => {
      const id = opportunityId; // id isn't in variables for this one as currently written
      queryClient.setQueryData([OPPORTUNITY_QUERY_KEY, id], data);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: [OPPORTUNITY_QUERY_KEY, id] }),
        queryClient.invalidateQueries({ queryKey: [OPPORTUNITIES_LIST_QUERY_KEY] }),
        queryClient.invalidateQueries({
          queryKey: [OPPORTUNITY_STATUS_HISTORY_QUERY_KEY, id],
        }),
        queryClient.invalidateQueries({
          queryKey: [OPPORTUNITY_CHANGE_HISTORY_QUERY_KEY, id],
        }),
      ]);
    },
    onError: (error: Error) => {
      handleApiError(error, { messagePrefix: 'Error updating auto match' });
      // Revert on error
      if (opportunity) {
        setAutoMatch(opportunity.settings?.auto_match ?? true);
      }
    },
  });

  const updateMaxAcceptedProvidersMutation = useMutation({
    mutationFn: async (value: number | null) => {
      return AssignmentsService.assignmentsOpportunitiesPartialUpdate({
        id: opportunityId,
        requestBody: {
          settings: {
            ...opportunity?.settings,
            max_accepted_connections: value,
          },
        } as PatchedOpportunityUpdate,
      });
    },
    onSuccess: async (data, variables) => {
      const id = opportunityId;
      queryClient.setQueryData([OPPORTUNITY_QUERY_KEY, id], data);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: [OPPORTUNITY_QUERY_KEY, id] }),
        queryClient.invalidateQueries({ queryKey: [OPPORTUNITIES_LIST_QUERY_KEY] }),
        queryClient.invalidateQueries({
          queryKey: [OPPORTUNITY_STATUS_HISTORY_QUERY_KEY, id],
        }),
        queryClient.invalidateQueries({
          queryKey: [OPPORTUNITY_CHANGE_HISTORY_QUERY_KEY, id],
        }),
      ]);
    },
    onError: (error: Error) => {
      handleApiError(error, { messagePrefix: 'Error updating maximum accepted providers' });
      // Revert on error
      if (opportunity) {
        setMaxAcceptedProviders(opportunity.settings?.max_accepted_connections?.toString() ?? '');
        lastSavedMaxAcceptedProvidersRef.current =
          opportunity.settings?.max_accepted_connections ?? null;
      }
    },
  });

  const updateStartDateMutation = useMutation({
    mutationFn: async (value: string | null) => {
      return AssignmentsService.assignmentsOpportunitiesPartialUpdate({
        id: opportunityId,
        requestBody: {
          starts_on: value,
        } as PatchedOpportunityUpdate,
      });
    },
    onSuccess: async (data, variables) => {
      const id = opportunityId;
      queryClient.setQueryData([OPPORTUNITY_QUERY_KEY, id], data);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: [OPPORTUNITY_QUERY_KEY, id] }),
        queryClient.invalidateQueries({ queryKey: [OPPORTUNITIES_LIST_QUERY_KEY] }),
        queryClient.invalidateQueries({
          queryKey: [OPPORTUNITY_STATUS_HISTORY_QUERY_KEY, id],
        }),
        queryClient.invalidateQueries({
          queryKey: [OPPORTUNITY_CHANGE_HISTORY_QUERY_KEY, id],
        }),
      ]);
    },
    onError: (error: Error) => {
      handleApiError(error, { messagePrefix: 'Error updating start date' });
      // Revert on error
      if (opportunity) {
        setStartDate(parseApiLocalDate(opportunity.starts_on));
        lastSavedStartDateRef.current = opportunity.starts_on ?? null;
      }
    },
  });

  const statusUpdateMutation = useMutation({
    mutationFn: async (data: {
      id: string;
      statusCode: string | null;
      reasonCode?: string | null;
      comment?: string;
    }) => {
      const statusUpdateData: PatchedOpportunityStatusUpdate = {
        code: data.statusCode || undefined,
        reason_code: data.reasonCode ?? null,
        comment: data.comment || null,
      };
      return AssignmentsService.assignmentsOpportunitiesStatusUpdatePartialUpdate({
        id: data.id,
        requestBody: statusUpdateData,
      });
    },
    onSuccess: async (updatedOpportunity, variables) => {
      const { id } = variables;
      toast.success('Opportunity status successfully updated');
      queryClient.setQueryData([OPPORTUNITY_QUERY_KEY, id], updatedOpportunity);
      const invalidations = [
        queryClient.invalidateQueries({ queryKey: [OPPORTUNITY_QUERY_KEY, id] }),
        queryClient.invalidateQueries({ queryKey: [OPPORTUNITIES_LIST_QUERY_KEY] }),
        queryClient.invalidateQueries({
          queryKey: [OPPORTUNITY_STATUS_HISTORY_QUERY_KEY, id],
        }),
        queryClient.invalidateQueries({
          queryKey: [OPPORTUNITY_CHANGE_HISTORY_QUERY_KEY, id],
        }),
        queryClient.invalidateQueries({ queryKey: ['staffingNeeds', 'list'] }),
        // Invalidate queue notification badges for this scope
        queryClient.invalidateQueries({
          queryKey: [QUEUE_NOTIFICATIONS_QUERY_KEY, WorkflowScopes.Opportunity],
        }),
        // Invalidate global menu badges
        queryClient.invalidateQueries({ queryKey: [MENU_BADGES_QUERY_KEY] }),
      ];
      if (updatedOpportunity.need?.id) {
        invalidations.push(
          queryClient.invalidateQueries({ queryKey: ['need', updatedOpportunity.need?.id] })
        );
      }
      await Promise.all(invalidations);
    },
    onError: (error: Error) => {
      handleApiError(error, { messagePrefix: 'Failed to update opportunity status' });
    },
  });

  const handleAutoMatchChange = (checked: boolean) => {
    setAutoMatch(checked);
    updateAutoMatchMutation.mutate(checked);
  };

  const handleMaxAcceptedProvidersChange = (value: string) => {
    setMaxAcceptedProviders(value);
    isMaxAcceptedProvidersDirtyRef.current = true;
  };

  const maxAcceptedProvidersCommit = useCallback(
    async (val?: number | null) => {
      const numericValue =
        val !== undefined ? val : maxAcceptedProviders === '' ? null : Number(maxAcceptedProviders);
      if (
        numericValue === lastSavedMaxAcceptedProvidersRef.current ||
        isSavingMaxAcceptedProvidersRef.current ||
        !isMaxAcceptedProvidersDirtyRef.current
      ) {
        return;
      }

      try {
        isSavingMaxAcceptedProvidersRef.current = true;
        await updateMaxAcceptedProvidersMutation.mutateAsync(numericValue);
        lastSavedMaxAcceptedProvidersRef.current = numericValue;
        isMaxAcceptedProvidersDirtyRef.current = false;
      } catch (e) {
        console.error('Max accepted providers save failed', e);
      } finally {
        isSavingMaxAcceptedProvidersRef.current = false;
      }
    },
    [updateMaxAcceptedProvidersMutation, maxAcceptedProviders]
  );

  const debouncedMaxAcceptedProvidersCommit = useDebouncedFn(maxAcceptedProvidersCommit, 600);

  const handleStartDateChange = (date: Date | null) => {
    setStartDate(date);
    isStartDateDirtyRef.current = true;
  };

  const startDateCommit = useCallback(
    async (val?: Date | null) => {
      const dateToSave = val !== undefined ? val : startDate;
      const dateStr = parseFormDate(dateToSave);

      if (
        dateStr === lastSavedStartDateRef.current ||
        isSavingStartDateRef.current ||
        !isStartDateDirtyRef.current
      ) {
        return;
      }

      try {
        isSavingStartDateRef.current = true;
        await updateStartDateMutation.mutateAsync(dateStr);
        lastSavedStartDateRef.current = dateStr;
        isStartDateDirtyRef.current = false;
      } catch (e) {
        console.error('Start date save failed', e);
      } finally {
        isSavingStartDateRef.current = false;
      }
    },
    [updateStartDateMutation, startDate]
  );

  const debouncedStartDateCommit = useDebouncedFn(startDateCommit, 600);

  const handleStartDateBlur = (value?: Date | null) => {
    debouncedStartDateCommit.flush(value);
  };

  const handleMaxAcceptedProvidersBlur = (value: string) => {
    if (!opportunity) {
      return;
    }

    // Validate that it's a number if not empty
    if (value !== '' && isNaN(Number(value))) {
      return;
    }

    debouncedMaxAcceptedProvidersCommit.flush(value);
  };

  // Flush when leaving the page or unmounting (Safari-friendly)
  useEffect(() => {
    const onPageHide = () => {
      debouncedMaxAcceptedProvidersCommit.flush();
      debouncedStartDateCommit.flush();
    };
    window.addEventListener('pagehide', onPageHide);
    document.addEventListener('visibilitychange', onPageHide);
    return () => {
      window.removeEventListener('pagehide', onPageHide);
      document.removeEventListener('visibilitychange', onPageHide);
      // Flush on unmount to ensure pending changes are saved
      debouncedMaxAcceptedProvidersCommit.flush();
      debouncedStartDateCommit.flush();
    };
  }, [debouncedMaxAcceptedProvidersCommit, debouncedStartDateCommit]);

  // Save on click/tap outside (covers “no blur” cases)
  useEffect(() => {
    const handler = (e: MouseEvent | TouchEvent) => {
      debouncedMaxAcceptedProvidersCommit.flush();
      debouncedStartDateCommit.flush();
    };
    document.addEventListener('mousedown', handler, true);
    document.addEventListener('touchstart', handler, true);
    return () => {
      document.removeEventListener('mousedown', handler, true);
      document.removeEventListener('touchstart', handler, true);
    };
  }, [debouncedMaxAcceptedProvidersCommit, debouncedStartDateCommit]);

  // Handle duplicate click
  const handleDuplicateClick = () => {
    navigate(`/app/organization/opportunities/new?duplicate=${opportunityId}`);
  };

  // Handle status modal confirm
  const handleStatusModalConfirm = async (data: {
    statusCode: string | null;
    reasonCode: string | null;
    comment?: string;
  }) => {
    if (!opportunityId) {
      toast.error('Opportunity ID not found');
      return;
    }

    await statusUpdateMutation.mutateAsync({
      id: opportunityId,
      statusCode: data.statusCode,
      reasonCode: data.reasonCode,
      comment: data.comment,
    });
    setIsStatusModalOpen(false);
    setPendingTargetStatus(null);
  };

  const handleStatusModalClose = () => {
    setIsStatusModalOpen(false);
    setPendingTargetStatus(null);
  };

  if (isLoadingOpportunity) {
    return (
      <div className="tw:flex tw:justify-center tw:py-8">
        <div className="tw:text-zinc-500 tw:dark:text-zinc-400">Loading opportunity details...</div>
      </div>
    );
  }

  if (opportunityError) {
    return (
      <div className="tw:flex tw:justify-center tw:py-8">
        <div className="tw:text-stoplight-danger">
          Error loading opportunity details. Please try again.
        </div>
      </div>
    );
  }

  if (!opportunity) {
    return null;
  }

  const isClosed = opportunity.current_status?.title === 'Closed';
  const specialtyMap = codeDefinitionMaps?.Specialty || {};
  const degreeMap = codeDefinitionMaps?.Degree || {};
  const shiftRateTypeMap = codeDefinitionMaps?.ShiftRateTypes || {};

  const handleEditModalClose = () => {
    setIsEditModalOpen(false);
    // Refresh opportunity data after closing modal
    queryClient.invalidateQueries({ queryKey: [OPPORTUNITY_QUERY_KEY, opportunityId] });
    queryClient.invalidateQueries({ queryKey: [OPPORTUNITIES_LIST_QUERY_KEY] });
  };

  const handleEditSuccess = () => {
    // Refresh opportunity data after successful edit
    queryClient.invalidateQueries({ queryKey: [OPPORTUNITY_QUERY_KEY, opportunityId] });
    queryClient.invalidateQueries({ queryKey: [OPPORTUNITIES_LIST_QUERY_KEY] });
    setIsEditModalOpen(false);
  };

  return (
    <>
      <StatusTransitionModal
        open={isStatusModalOpen}
        onClose={handleStatusModalClose}
        onConfirm={handleStatusModalConfirm}
        initialStatusCode={opportunity?.current_status?.code || null}
        targetStatusCode={pendingTargetStatus}
        workflowScope="assignments.opportunity"
        isPending={statusUpdateMutation.isPending}
        currentReasonCode={null}
        currentStatusTitle={opportunity?.current_status?.title || null}
      />
      <Dialog
        open={isEditModalOpen}
        onClose={handleEditModalClose}
        className="tw:!max-w-6xl tw:!max-h-[calc(100vh-4rem)] tw:!flex tw:!flex-col tw:!p-0"
      >
        <div className="tw:flex tw:flex-col tw:flex-1 tw:min-h-0 tw:overflow-hidden">
          {headerProps && (
            <div className="tw:flex-shrink-0">
              <PageHeader
                className="tw:rounded-t-2xl"
                title={headerProps.title}
                buttonHtml={headerProps.buttonHtml}
              />
            </div>
          )}
          <div className="tw:flex-1 tw:overflow-y-auto tw:min-h-0">
            <DialogBody className="tw:mt-0">
              <OpportunityForm
                opportunityId={opportunityId}
                forceEditMode={true}
                onSuccess={handleEditSuccess}
                onCancel={handleEditModalClose}
                renderHeader={false}
                onHeaderRender={setHeaderProps}
              />
            </DialogBody>
          </div>
        </div>
      </Dialog>
      <div className="tw:flex tw:flex-col tw:mt-6">
        {/* Top Section */}
        <div className="tw:grid tw:grid-cols-1 tw:lg:grid-cols-12 tw:gap-6">
          {/* Opportunity Details */}
          <div className="tw:lg:col-span-6">
            <StaffingEntityQuickDetails
              code={opportunity.code}
              facilityName={opportunity.facility?.name}
              location={getFacilityLocationDisplayValue(opportunity.facility)}
              specialty={specialtyMap[opportunity.specialty] || opportunity.specialty}
            />
          </div>

          {/* Status and Controls */}
          <div className="tw:flex tw:flex-col tw:lg:col-span-5">
            <div className="tw:grid tw:grid-cols-[auto_1fr] tw:gap-x-6 tw:gap-y-2 tw:items-center">
              <div className="tw:text-sm tw:font-semibold">Status:</div>
              <div>
                <StatusPill
                  scope={WorkflowScopes.Opportunity}
                  status={opportunity.current_status}
                  small
                />
              </div>

              <div className="tw:text-sm tw:font-semibold">Auto-Match:</div>
              <div className="tw:flex tw:items-center tw:gap-3">
                <BrandSwitch
                  checked={autoMatch ?? true}
                  onChange={handleAutoMatchChange}
                  disabled={isClosed || updateAutoMatchMutation.isPending}
                  small
                />
                <span className="tw:text-sm tw:text-zinc-500">{autoMatch ? 'On' : 'Off'}</span>
              </div>

              <div className="tw:text-sm tw:font-semibold">Max Providers:</div>
              <InlineTextEdit
                value={maxAcceptedProviders}
                onChange={handleMaxAcceptedProvidersChange}
                onBlur={handleMaxAcceptedProvidersBlur}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    const value = (e.target as HTMLInputElement).value;
                    handleMaxAcceptedProvidersBlur(value);
                  }
                }}
                placeholder="Leave blank for unlimited..."
                displayValue={(val) => val || 'Unlimited'}
                small
                editOn="hover"
                disabled={isClosed || updateMaxAcceptedProvidersMutation.isPending}
                type="text"
                inputMode="numeric"
              />

              <div className="tw:text-sm tw:font-semibold">Start Date:</div>
              <InlineDateEdit
                value={startDate}
                onChange={handleStartDateChange}
                onBlur={handleStartDateBlur}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleStartDateBlur(startDate);
                }}
                placeholder="Select start date"
                small
                editOn="hover"
                disabled={isClosed || updateStartDateMutation.isPending}
              />
            </div>
          </div>

          {/* Close Button */}
          {onClose && (
            <div className="tw:flex tw:justify-end tw:items-start tw:lg:col-span-1">
              <DrawerCloseButton onClick={onClose} />
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div className="tw:flex tw:flex-wrap tw:gap-2 tw:mt-2 tw:justify-end">
          <StatusTransitionButtons
            isLoading={isLoadingOpportunity}
            currentStatusCode={opportunity?.current_status?.code}
            workflowMetadata={workflowStatuses as StatusMetadata}
            onStatusClick={(status) => {
              setPendingTargetStatus(status.code);
              setIsStatusModalOpen(true);
            }}
            isPending={statusUpdateMutation.isPending}
          />
          {isClosed && (
            <Button small color="brand-green-outline" onClick={handleDuplicateClick}>
              Duplicate
            </Button>
          )}
        </div>

        {/* Details and Connections */}
        <Tabs defaultTab="details" className="tw:mt-4">
          <TabsList>
            <TabsButton tab="details">Details</TabsButton>
          </TabsList>
          <TabsContent tab="details">
            <div className="tw:flex tw:justify-between tw:items-start tw:gap-6">
              <div className="tw:flex-1 tw:space-y-4">
                {opportunity?.need && (
                  <section>
                    <div className="tw:mb-4">
                      <a
                        href={`/app/organization/needs?id=${opportunity?.need?.id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="tw:mt-0 tw:!text-sm tw:text-brand-green-0 hover:tw:underline tw:text-left tw:bg-transparent tw:border-0 tw:p-0 tw:cursor-pointer tw:dark:text-brand-green-1"
                      >
                        <i className="far fa-arrow-up-right-from-square tw:mr-2" />
                        Linked Need: {opportunity?.need?.code}
                      </a>
                    </div>
                  </section>
                )}

                {/* Basic Information Section */}
                <section>
                  <DrawerSectionHeader
                    text="Basic Information"
                    icon={
                      <i className="fas fa-info-circle tw:text-brand-green-0 tw:dark:text-brand-green-1" />
                    }
                  />
                  <div className="tw:grid tw:grid-cols-[auto_1fr] tw:gap-x-6 tw:gap-y-2 tw:text-sm">
                    <DrawerField
                      label="Estimated Duration"
                      value={opportunity.estimated_duration}
                    />
                    <DrawerField
                      label="Description"
                      value={opportunity.description}
                      whitespacePreLine
                    />
                    <DrawerField
                      label="Additional Facilities"
                      value={
                        opportunity.alternate_facilities &&
                        Array.isArray(opportunity.alternate_facilities) &&
                        opportunity.alternate_facilities.length > 0
                          ? opportunity.alternate_facilities
                              .map((facility) =>
                                typeof facility === 'string' ? facility : facility.name
                              )
                              .join(', ')
                          : null
                      }
                    />
                  </div>
                </section>

                {/* Professional Requirements Section */}
                <section>
                  <DrawerSectionHeader
                    text="Professional Requirements"
                    icon={
                      <i className="fas fa-stethoscope tw:text-brand-green-0 tw:dark:text-brand-green-1" />
                    }
                  />
                  <div className="tw:grid tw:grid-cols-[auto_1fr] tw:gap-x-6 tw:gap-y-2 tw:text-sm">
                    <DrawerField
                      label="Degrees"
                      value={
                        opportunity.degrees && opportunity.degrees.length > 0
                          ? opportunity.degrees.map((deg) => degreeMap[deg] || deg).join(', ')
                          : null
                      }
                    />
                    <DrawerField
                      label="Availability"
                      value={
                        opportunity.work_types && opportunity.work_types.length > 0
                          ? opportunity.work_types.map((workType) => workType).join(', ')
                          : null
                      }
                    />
                    <DrawerField
                      label="Life Support Certifications"
                      value={
                        opportunity.life_support_certifications &&
                        opportunity.life_support_certifications.length > 0
                          ? opportunity.life_support_certifications.map((cert) => cert).join(', ')
                          : null
                      }
                    />
                    <DrawerField
                      label="Board Certifications"
                      value={
                        opportunity.board_certifications &&
                        opportunity.board_certifications.length > 0
                          ? opportunity.board_certifications
                              .map((cert) =>
                                cert.boardEligible ? `${cert.value} (Allow Eligible)` : cert.value
                              )
                              .join(', ')
                          : null
                      }
                    />
                  </div>
                </section>

                {/* Eligibility Requirements Section */}
                <section>
                  <DrawerSectionHeader
                    text="Eligibility Requirements"
                    icon={
                      <i className="fas fa-check-circle tw:text-brand-green-0 tw:dark:text-brand-green-1" />
                    }
                  />
                  <div className="tw:grid tw:grid-cols-[auto_1fr] tw:gap-x-6 tw:gap-y-2 tw:text-sm">
                    <DrawerField
                      label="Allow IMLC Providers"
                      value={opportunity.allow_imlc ? 'Yes' : 'No'}
                    />
                    <DrawerField
                      label="Allow Malpractice History"
                      value={opportunity.allow_malpractice_history ? 'Yes' : 'No'}
                    />
                    <DrawerField
                      label="Years of Experience"
                      value={opportunity.years_of_experience}
                    />
                    <DrawerField label="Shifts Completed" value={opportunity.shifts_completed} />
                    <DrawerField label="Additional Info" value={opportunity.additional_info} />
                  </div>
                </section>

                {/* Opportunity Settings Section */}
                <section>
                  <DrawerSectionHeader
                    text="Opportunity Settings"
                    icon={
                      <i className="fas fa-cog tw:text-brand-green-0 tw:dark:text-brand-green-1" />
                    }
                  />
                  <div className="tw:grid tw:grid-cols-[auto_1fr] tw:gap-x-6 tw:gap-y-2 tw:text-sm">
                    <DrawerField
                      label="Shift Rate Types"
                      value={
                        opportunity.shift_rate_types && opportunity.shift_rate_types.length > 0
                          ? opportunity.shift_rate_types
                              .map((shiftRateType) => {
                                const shiftRateTypeLabel =
                                  shiftRateTypeMap[shiftRateType] || shiftRateType;
                                // Map shift rate type to settings key for max rate
                                const shiftTypeToSettingsKey: Record<string, string> = {
                                  day: 'max_rate_day',
                                  night: 'max_rate_night',
                                  call: 'max_rate_call',
                                  '24h': 'max_rate_24h',
                                };
                                const rateKey = shiftTypeToSettingsKey[shiftRateType];
                                const rate = opportunity.settings?.[
                                  rateKey as keyof typeof opportunity.settings
                                ] as number | null | undefined;
                                const rateDisplay = rate ? `: $${rate}` : '';
                                return `${shiftRateTypeLabel}${rateDisplay}`;
                              })
                              .join(', ')
                          : null
                      }
                    />
                    <DrawerField
                      label="Created"
                      value={
                        opportunity.created_at
                          ? formatDateTo24Hr(opportunity.created_at, 'MM/dd/yyyy HH:mm')
                          : null
                      }
                    />
                    <DrawerField
                      label="Last Updated"
                      value={
                        opportunity.updated_at
                          ? formatDateTo24Hr(opportunity.updated_at, 'MM/dd/yyyy HH:mm')
                          : null
                      }
                    />
                  </div>
                </section>
              </div>
              {opportunity.current_status?.title !== 'Closed' && (
                <button
                  type="button"
                  onClick={() => setIsEditModalOpen(true)}
                  className="tw:text-brand-green-0 tw:hover:text-brand-green-0/80 tw:transition-colors tw:cursor-pointer"
                  aria-label="Edit opportunity"
                >
                  <i className="fa-solid fa-pen-to-square tw:text-lg" />
                </button>
              )}
            </div>
          </TabsContent>
        </Tabs>

        {/* Comment form */}
        <form
          onSubmit={noteForm.handleSubmit((data) => createNoteMutation.mutate(data))}
          className="tw:mt-4"
        >
          <Fieldset>
            <div className="tw:grid tw:grid-cols-1 tw:gap-4">
              <Field>
                <Textarea
                  {...noteForm.register('comment')}
                  placeholder="Enter a comment..."
                  rows={4}
                  disabled={createNoteMutation.isPending}
                />
                {noteForm.formState.errors.comment && (
                  <FieldValidationErrorMessage
                    message={noteForm.formState.errors.comment.message}
                  />
                )}
              </Field>
            </div>
            <div className="tw:flex tw:justify-end tw:mt-4">
              <Button type="submit" color="brandgreen0" disabled={createNoteMutation.isPending}>
                {createNoteMutation.isPending ? 'Adding...' : 'Add Comment'}
              </Button>
            </div>
          </Fieldset>
        </form>

        {/* Comments and Activity */}
        <Tabs defaultTab="activity">
          <TabsList>
            <TabsButton tab="activity">Activity</TabsButton>
          </TabsList>
          <TabsContent tab="activity" className="tw:pt-0 tw:!mt-0">
            <ActivityLog
              notes={notesData}
              enableCommentsFilter={true}
              enableStatusChangesFilter={true}
              enableUpdatesFilter={true}
              enableEventsFilter={false}
              statusHistory={statusHistory as ActivityStatusHistory[] | undefined}
              changeHistory={changeHistory as ActivityHistory[] | undefined}
              noteMutations={{
                updateNote: async (noteId: string, data: PatchedOpportunityNote) => {
                  return AssignmentsService.assignmentsOpportunitiesNotePartialUpdate({
                    id: noteId,
                    requestBody: data,
                  });
                },
                deleteNote: async (noteId: string) => {
                  return AssignmentsService.assignmentsOpportunitiesNoteDestroy({
                    id: noteId,
                  });
                },
                notesQueryKey: [OPPORTUNITY_NOTES_QUERY_KEY, opportunityId],
                extraInvalidateQueryKeys: [[OPPORTUNITY_STATUS_HISTORY_QUERY_KEY, opportunityId]],
              }}
            />
          </TabsContent>
        </Tabs>
      </div>
    </>
  );
}
